import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Paper
} from '@mui/material';
import {
  YouTube as YouTubeIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  VideoFile as VideoFileIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import LanguageSelector from '../dubbing/LanguageSelector';
import { toast } from 'react-hot-toast';
import { sanitizeErrorMessage } from '../../utils/errorMessage';

function CreateAudioJobModalEnhanced({ open, onClose, onSubmit }) {
  // State for source type
  const [sourceType, setSourceType] = useState('youtube'); // 'youtube' or 'upload'
  
  // YouTube states
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Common states
  const [sourceLanguage, setSourceLanguage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [languages, setLanguages] = useState([]);

  // Allowed video formats
  const ALLOWED_FORMATS = '.mp4,.avi,.mov,.mkv,.webm,.flv,.wmv,.m4v';
  const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

  useEffect(() => {
    if (open) {
      fetchLanguages();
    }
  }, [open]);

  const fetchLanguages = async () => {
    try {
      const response = await apiService.getSupportedLanguages();
      setLanguages(response.supported_languages || []);
    } catch (err) {
      console.error('Failed to fetch languages:', err);
      toast.error('Failed to load languages');
    }
  };

  const handleSourceTypeChange = (event, newType) => {
    if (newType !== null) {
      setSourceType(newType);
      setError('');
      // Reset source-specific states
      setYoutubeUrl('');
      setValidationResult(null);
      setSelectedFile(null);
      setUploadResult(null);
    }
  };

  const handleUrlChange = async (url) => {
    setYoutubeUrl(url);
    setValidationResult(null);
    setError('');

    if (!url) return;

    const youtubePattern = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)[\w-]+/;
    if (!youtubePattern.test(url)) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    setValidating(true);
    try {
      const result = await apiService.validateYouTubeUrl(url);
      if (result.valid) {
        setValidationResult(result);
      } else {
        setError('Invalid YouTube URL or video not accessible');
      }
    } catch (err) {
      setError('Failed to validate YouTube URL');
    } finally {
      setValidating(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setUploadResult(null);

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 2GB limit');
      return;
    }

    // Check file type
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_FORMATS.includes(ext)) {
      setError('Invalid file type. Supported formats: MP4, AVI, MOV, MKV, WebM, FLV, WMV, M4V');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await apiService.uploadVideoForTranslation(formData, (progress) => {
        setUploadProgress(progress);
      });
      
      setUploadResult(result);
      toast.success('Video uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      console.error('Upload error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: err.config
      });
      
      // Better error message handling
      let errorMessage = 'Failed to upload video';
      if (err.response?.status === 404) {
        errorMessage = 'Upload endpoint not found. Please ensure the backend is running.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (err.response?.data?.detail) {
        errorMessage = sanitizeErrorMessage(err.response.data.detail);
      } else if (err.message) {
        errorMessage = sanitizeErrorMessage(err.message);
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    const hasValidSource = (sourceType === 'youtube' && validationResult) || 
                          (sourceType === 'upload' && uploadResult);
    
    if (!hasValidSource || !sourceLanguage || !targetLanguage) {
      toast.error('Please fill all required fields');
      return;
    }

    if (sourceLanguage === targetLanguage) {
      toast.error('Source and target languages must be different');
      return;
    }

    setSubmitting(true);
    try {
      if (sourceType === 'youtube') {
        await onSubmit({
          youtube_url: youtubeUrl,
          source_language: sourceLanguage,
          target_language: targetLanguage
        });
      } else {
        // For uploaded videos, use the onSubmit function which will call createAudioTranslationFromUpload
        await onSubmit({
          video_url: uploadResult.video_url,
          video_title: uploadResult.filename || uploadResult.title || 'Uploaded Video',
          video_duration: uploadResult.duration,
          source_language: sourceLanguage,
          target_language: targetLanguage,
          video_source: 'upload'
        });
      }
      
      handleClose();
      toast.success('Audio translation job created successfully');
    } catch (err) {
      toast.error(sanitizeErrorMessage(err.message) || 'Failed to create audio translation job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting && !uploading) {
      // Reset all states
      setSourceType('youtube');
      setYoutubeUrl('');
      setValidationResult(null);
      setSelectedFile(null);
      setUploadResult(null);
      setSourceLanguage('');
      setTargetLanguage('');
      setError('');
      onClose();
    }
  };

  const canSubmit = (
    ((sourceType === 'youtube' && validationResult) || 
     (sourceType === 'upload' && uploadResult)) &&
    sourceLanguage && 
    targetLanguage && 
    !submitting && 
    !uploading
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideoFileIcon color="primary" />
          <Typography variant="h6">Create Audio Translation</Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting || uploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Source Type Toggle */}
          <ToggleButtonGroup
            value={sourceType}
            exclusive
            onChange={handleSourceTypeChange}
            fullWidth
            sx={{ mb: 3 }}
            disabled={submitting || uploading}
          >
            <ToggleButton value="youtube">
              <YouTubeIcon sx={{ mr: 1 }} />
              YouTube URL
            </ToggleButton>
            <ToggleButton value="upload">
              <CloudUploadIcon sx={{ mr: 1 }} />
              Upload Video
            </ToggleButton>
          </ToggleButtonGroup>

          {/* YouTube Input */}
          {sourceType === 'youtube' && (
            <>
              <TextField
                fullWidth
                label="YouTube URL"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                disabled={submitting}
                error={!!error}
                helperText={error}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon color={error ? 'error' : 'action'} />
                    </InputAdornment>
                  ),
                  endAdornment: validating && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />

              {validationResult && (
                <Alert 
                  severity="success" 
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {validationResult.title}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                    <Chip 
                      size="small" 
                      label={`Duration: ${formatDuration(validationResult.duration)}`} 
                    />
                    <Chip 
                      size="small" 
                      label={validationResult.uploader} 
                    />
                  </Box>
                </Alert>
              )}
            </>
          )}

          {/* File Upload */}
          {sourceType === 'upload' && (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  mb: 3,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  bgcolor: 'background.default',
                  cursor: 'pointer'
                }}
                component="label"
              >
                <input
                  type="file"
                  hidden
                  accept={ALLOWED_FORMATS}
                  onChange={handleFileSelect}
                  disabled={uploading || submitting}
                />
                <VideoFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                
                {!selectedFile ? (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Click to select a video file
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supported formats: MP4, AVI, MOV, MKV, WebM, FLV, WMV, M4V
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Maximum file size: 2GB
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" gutterBottom>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Size: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                    {!uploadResult && !uploading && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={handleFileUpload}
                      >
                        Upload Video
                      </Button>
                    )}
                  </>
                )}
              </Paper>

              {uploading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Uploading... {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              {uploadResult && (
                <Alert 
                  severity="success" 
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {uploadResult.title}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      size="small" 
                      label={`Duration: ${formatDuration(uploadResult.duration)}`} 
                    />
                  </Box>
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
            </>
          )}

          {/* Language Selection */}
          {((sourceType === 'youtube' && validationResult) || 
            (sourceType === 'upload' && uploadResult)) && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <LanguageSelector
                value={sourceLanguage}
                onChange={(e) => setSourceLanguage(e.target.value)}
                label="Source Language"
                languages={languages}
                showDetect={true}
                disabled={submitting}
              />
              <LanguageSelector
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                label="Target Language"
                languages={languages}
                disabled={submitting}
              />
            </Box>
          )}

          {/* Info */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This will extract audio from the {sourceType === 'youtube' ? 'YouTube video' : 'uploaded video'} 
              and translate it to your selected language. No video processing or lip sync will be performed.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={submitting || uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting && <CircularProgress size={20} />}
        >
          {submitting ? 'Creating...' : 'Create Translation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateAudioJobModalEnhanced;